/**
 * HistoricalFXChart — Phase 36 Batch Y (GMIA Phase 2).
 *
 * Dual-axis ComposedChart showing 4 toggleable series for a cross-currency
 * deal over a configurable timeframe:
 *
 *   • Property value (local)  — left Y axis, in property_currency
 *   • Property value (base)   — left Y axis, in user's base_currency
 *   • FX rate                 — right Y axis (property/base ratio)
 *   • Total return %          — right Y axis, percentage
 *
 * Timeframes: 1Y / 3Y / 5Y / Since Purchase. The PRD also mentions
 * 30D/90D but Frankfurter+bridge granularity is monthly, so we hide
 * those buttons when no data exists at that resolution.
 *
 * Hidden entirely when same-currency (caller controls; we also guard).
 */
import { useEffect, useMemo, useState } from "react";
import {
  ComposedChart, Line, Area, XAxis, YAxis,
  CartesianGrid, Tooltip as RcTooltip, Legend, ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, TrendingUp, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

const TIMEFRAMES = [
  { key: "1Y", years: 1, label: "1Y" },
  { key: "3Y", years: 3, label: "3Y" },
  { key: "5Y", years: 5, label: "5Y" },
  { key: "10Y", years: 10, label: "10Y" },
  { key: "PURCHASE", years: 50, label: "Since Purchase" },
];

const SERIES_DEFAULTS = {
  property_value_local: true,
  property_value_base: true,
  fx_rate: true,
  total_return_pct: true,
};

const SERIES_META = {
  property_value_local: { color: "#94a3b8", label: "Value (local)", axis: "left" },
  property_value_base:  { color: "#4f46e5", label: "Value (base)",  axis: "left" },
  fx_rate:              { color: "#0ea5e9", label: "FX rate",       axis: "right" },
  total_return_pct:     { color: "#10b981", label: "Total return",  axis: "right" },
};

function fmtMoneyShort(n, ccy) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  const abs = Math.abs(Number(n));
  let units = 1, suffix = "";
  if (abs >= 1e9) { units = 1e9; suffix = "B"; }
  else if (abs >= 1e6) { units = 1e6; suffix = "M"; }
  else if (abs >= 1e3) { units = 1e3; suffix = "K"; }
  const v = Number(n) / units;
  return `${ccy ? ccy + " " : ""}${v.toFixed(v >= 100 ? 0 : 1)}${suffix}`;
}

function fmtPct(n) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  const sign = Number(n) >= 0 ? "+" : "";
  return `${sign}${Number(n).toFixed(1)}%`;
}

function ChartTooltip({ active, payload, label, propCurr, baseCurr }) {
  if (!active || !payload || payload.length === 0) return null;
  const byKey = Object.fromEntries(payload.map((p) => [p.dataKey, p.value]));
  return (
    <div className="rounded-xl border border-slate-200 bg-white/95 px-3 py-2 shadow-md backdrop-blur">
      <p className="text-xs font-semibold text-slate-900">{label}</p>
      <div className="mt-1.5 space-y-1">
        {"property_value_local" in byKey ? (
          <div className="flex items-center justify-between gap-4 text-xs">
            <span className="text-slate-500">Value ({propCurr})</span>
            <span className="font-medium tabular-nums text-slate-900">
              {fmtMoneyShort(byKey.property_value_local, propCurr)}
            </span>
          </div>
        ) : null}
        {"property_value_base" in byKey ? (
          <div className="flex items-center justify-between gap-4 text-xs">
            <span className="text-slate-500">Value ({baseCurr})</span>
            <span className="font-medium tabular-nums text-indigo-700">
              {fmtMoneyShort(byKey.property_value_base, baseCurr)}
            </span>
          </div>
        ) : null}
        {"fx_rate" in byKey ? (
          <div className="flex items-center justify-between gap-4 text-xs">
            <span className="text-slate-500">FX rate</span>
            <span className="font-medium tabular-nums text-sky-700">
              {Number(byKey.fx_rate).toFixed(4)}
            </span>
          </div>
        ) : null}
        {"total_return_pct" in byKey ? (
          <div className="flex items-center justify-between gap-4 text-xs">
            <span className="text-slate-500">Total return</span>
            <span className="font-medium tabular-nums text-emerald-700">
              {fmtPct(byKey.total_return_pct)}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function HistoricalFXChart({ dealId, fxView }) {
  const [tf, setTf] = useState("5Y");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [series, setSeries] = useState(SERIES_DEFAULTS);
  const [error, setError] = useState(null);

  const fxApplicable = !!fxView?.fx_applicable;
  const propCurr = fxView?.property_currency || "—";
  const baseCurr = fxView?.base_currency || "USD";

  useEffect(() => {
    let cancelled = false;
    if (!dealId || !fxApplicable) return undefined;
    const yrs = TIMEFRAMES.find((t) => t.key === tf)?.years || 5;
    setLoading(true);
    setError(null);
    api
      .get(`/deals/${dealId}/history`, { params: { years: yrs } })
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || "Failed to load history");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dealId, tf, fxApplicable]);

  const chartData = useMemo(() => (data?.points || []).map((p) => ({
    date: p.date,
    property_value_local: p.property_value_local,
    property_value_base: p.property_value_base,
    fx_rate: p.fx_rate,
    total_return_pct: p.total_return_pct,
  })), [data]);

  if (!fxApplicable) return null;

  return (
    <Card
      className="overflow-hidden"
      data-testid="historical-fx-chart"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-slate-50/60 px-5 py-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-indigo-600" />
          <h2 className="text-sm font-semibold text-slate-900">
            Historical performance
          </h2>
          {data?.coverage === "limited" ? (
            <span
              className="ml-1 inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800"
              data-testid="historical-fx-limited-badge"
            >
              <AlertTriangle className="h-3 w-3" />
              Limited data
            </span>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-1" data-testid="historical-fx-timeframes">
          {TIMEFRAMES.map((t) => (
            <Button
              key={t.key}
              variant={tf === t.key ? "default" : "outline"}
              size="sm"
              className={`h-7 px-2.5 text-xs ${
                tf === t.key
                  ? "bg-indigo-600 text-white hover:bg-indigo-700"
                  : "bg-white"
              }`}
              data-testid={`historical-fx-tf-${t.key.toLowerCase()}`}
              onClick={() => setTf(t.key)}
            >
              {t.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="px-5 py-4">
        {/* Series toggles */}
        <div className="mb-3 flex flex-wrap gap-2" data-testid="historical-fx-toggles">
          {Object.entries(SERIES_META).map(([key, meta]) => {
            const active = !!series[key];
            return (
              <button
                key={key}
                type="button"
                onClick={() => setSeries((s) => ({ ...s, [key]: !s[key] }))}
                data-testid={`historical-fx-toggle-${key}`}
                data-active={active}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors ${
                  active
                    ? "border-slate-300 bg-white text-slate-900 shadow-sm"
                    : "border-slate-200 bg-slate-50 text-slate-400"
                }`}
              >
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ background: active ? meta.color : "#cbd5e1" }}
                />
                {meta.label}
              </button>
            );
          })}
        </div>

        {/* Chart */}
        <div className="h-72 w-full" data-testid="historical-fx-chart-area">
          {loading ? (
            <div className="flex h-full items-center justify-center text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="ml-2 text-sm">Loading history…</span>
            </div>
          ) : error ? (
            <div className="flex h-full items-center justify-center text-sm text-rose-600">
              {error}
            </div>
          ) : chartData.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-slate-400">
              No data available for this timeframe.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: "#64748b" }}
                  tickFormatter={(d) => (d || "").slice(0, 7)}
                  minTickGap={32}
                />
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: 10, fill: "#64748b" }}
                  tickFormatter={(v) => fmtMoneyShort(v, "")}
                  width={64}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 10, fill: "#64748b" }}
                  tickFormatter={(v) => Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 })}
                  width={48}
                />
                <RcTooltip
                  content={(props) => (
                    <ChartTooltip {...props} propCurr={propCurr} baseCurr={baseCurr} />
                  )}
                />
                {data?.purchase_date ? (
                  <ReferenceLine
                    x={data.purchase_date}
                    yAxisId="left"
                    stroke="#a78bfa"
                    strokeDasharray="4 4"
                    label={{
                      value: "Purchased",
                      position: "top",
                      fill: "#7c3aed",
                      fontSize: 10,
                    }}
                  />
                ) : null}
                {series.property_value_local ? (
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="property_value_local"
                    stroke={SERIES_META.property_value_local.color}
                    fill="#f1f5f9"
                    strokeWidth={1.5}
                    dot={false}
                  />
                ) : null}
                {series.property_value_base ? (
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="property_value_base"
                    stroke={SERIES_META.property_value_base.color}
                    strokeWidth={2}
                    dot={false}
                  />
                ) : null}
                {series.fx_rate ? (
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="fx_rate"
                    stroke={SERIES_META.fx_rate.color}
                    strokeWidth={1.5}
                    strokeDasharray="2 4"
                    dot={false}
                  />
                ) : null}
                {series.total_return_pct ? (
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="total_return_pct"
                    stroke={SERIES_META.total_return_pct.color}
                    strokeWidth={1.5}
                    dot={false}
                  />
                ) : null}
                <Legend
                  wrapperStyle={{ fontSize: "11px", color: "#475569" }}
                  iconSize={8}
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>

        {data?.coverage === "limited" ? (
          <p
            className="mt-2 text-xs text-slate-500"
            data-testid="historical-fx-limited-note"
          >
            {propCurr} is outside ECB coverage. Series is interpolated between
            your locked rate ({data.purchase_exchange_rate?.toLocaleString()}{" "}
            {propCurr}/1 {baseCurr}) and today's live rate. Daily granularity
            unlocks for {propCurr} in a future release.
          </p>
        ) : null}
      </div>
    </Card>
  );
}
